import { useState } from "react";
import { Stack, Step, StepLabel, Stepper, Alert } from "@mui/material";
import { type ShiftInviteDialogProps } from "../Dialogs";
import { isDefined } from "@clipboard-health/util-ts";
import { ShiftInviteNearbyWorkers } from "../Autocomplete";
import { NearbyWorkerSearchResult } from "../Autocomplete/useGetNearbyWorkers";
import { ShiftInviteReview } from "../ShiftInviteReview";
import { GroupShiftInviteDetails } from "../GroupShiftInviteDetail";
import { useShiftInviteContext } from "../Context/shiftInviteContext";
import { ShiftName } from "@src/interface";
import { utcToZonedTime } from "date-fns-tz";
import { isAfter, startOfDay, subDays } from "date-fns";
import { useShiftInviteFrictionConfiguration } from "../useShiftInviteFrictionConfiguration";
import { WorkerAttendanceProfile } from "@src/containers/attendanceScore/types";
import { HighCallOffConfirmation } from "./HighCallOffConfirmation";
import {
  useWorkerAttendanceProfile,
  hasHighCallOff,
  getTotalCallOffs,
} from "../useWorkerAttendanceProfile";
import { WorkerStages, WorkerSubstages } from "@src/appV2/redesign/utils/worker";
import { InviteRequiredConfirmation } from "./InviteRequiredConfirmation";

export interface ShiftInviteStepperProps extends ShiftInviteDialogProps {
  onActiveStepChange?: (step: number) => void;
}

export function ShiftInviteStepper(props: ShiftInviteStepperProps) {
  const {
    workerType,
    modalState,
    initialActiveState = 0,
    initialSelectedWorker,
    onActiveStepChange,
  } = props;
  const [activeStep, setActiveStepState] = useState(initialActiveState);
  const [isProfileFetched, setIsProfileFetched] = useState<boolean>(false);
  const [selectedWorker, setSelectedWorker] = useState<NearbyWorkerSearchResult | undefined>(
    initialSelectedWorker
  );
  const [workerAttendanceProfile, setWorkerAttendanceProfile] = useState<WorkerAttendanceProfile>({
    clipboardScore: 0,
  });
  const { validateAndUpdateShiftInvite, facilityTimeZone } = useShiftInviteContext();
  const frictionConfig = useShiftInviteFrictionConfiguration();
  const zoneShiftStartTime = utcToZonedTime(props.shiftInfo.start, facilityTimeZone);
  const nowInFacilityTimeZone = utcToZonedTime(new Date(), facilityTimeZone);
  const selectedShiftStartsInFuture =
    isDefined(zoneShiftStartTime) && isAfter(zoneShiftStartTime, nowInFacilityTimeZone);

  const setActiveStep = (step: number) => {
    setActiveStepState(step);
    onActiveStepChange?.(step);
  };

  // Function to set selected worker
  const handleSetSelectedWorker = (worker: NearbyWorkerSearchResult | undefined) => {
    // Reset profile fetched flag when worker changes
    if (worker?.workerId !== selectedWorker?.workerId) {
      setIsProfileFetched(false);
    }
    setSelectedWorker(worker);
  };

  const currentWorkerId = selectedWorker?.workerId || initialSelectedWorker?.workerId;

  // Calculate if we need to fetch the profile
  const shouldFetchWorkerProfile = Boolean(
    currentWorkerId &&
    !isProfileFetched &&
    (activeStep === 0 || activeStep === 1) && // Only fetch in steps 0 or 1
    (Object.keys(workerAttendanceProfile).length === 0 || // Initial load
      (selectedWorker?.workerId !== undefined && // Worker changed
        selectedWorker.workerId !== initialSelectedWorker?.workerId))
  );

  const withPerformanceSince =
    frictionConfig.enabled &&
    "numberOfPastDaysToEvaluate" in frictionConfig &&
    isDefined(frictionConfig.numberOfPastDaysToEvaluate)
      ? subDays(new Date(), frictionConfig.numberOfPastDaysToEvaluate).toISOString()
      : undefined;

  useWorkerAttendanceProfile({
    workerId: currentWorkerId,
    enabled: shouldFetchWorkerProfile,
    onSuccess: (data) => {
      setWorkerAttendanceProfile(data);
      setIsProfileFetched(true); // Mark as fetched to prevent repeated calls
    },
    withPerformanceSince,
  });

  // Determine if the worker has high call-offs
  const maxAllowedPercentageOfCallOffs = frictionConfig.enabled
    ? frictionConfig.maxAllowedPercentageOfCallOffs
    : undefined;

  const maxAllowedCallOffs = frictionConfig.enabled ? frictionConfig.maxAllowedCallOffs : undefined;

  const hasHighCallOffRate = hasHighCallOff(
    workerAttendanceProfile,
    maxAllowedCallOffs,
    maxAllowedPercentageOfCallOffs
  );

  const isInviteRequired =
    workerAttendanceProfile.accountStatus?.status === WorkerStages.RESTRICTED &&
    workerAttendanceProfile.accountSubstatus?.name === WorkerSubstages.INVITE_REQUIRED;

  const totalCallOffs = getTotalCallOffs(workerAttendanceProfile);
  const workerFirstName = selectedWorker?.fullName?.split(" ")[0] || "Worker";

  const showHighCallOffConfirmation = isDefined(selectedWorker) && hasHighCallOffRate;
  const showInviteRequiredConfirmation = isDefined(selectedWorker) && isInviteRequired;
  const showSearchWorkersDropdown = !(
    showHighCallOffConfirmation || showInviteRequiredConfirmation
  );

  // Function to clear selected worker and return to search
  const handleInviteSomeoneElse = () => {
    setSelectedWorker(undefined);
    setWorkerAttendanceProfile({ clipboardScore: 0 });
    setActiveStepState(0);
  };

  return (
    <Stack spacing={2}>
      <Stepper activeStep={activeStep}>
        <Step>
          <StepLabel error={activeStep === 0 && showInviteRequiredConfirmation}>
            {isDefined(selectedWorker)
              ? `Inviting ${selectedWorker.fullName}, ${workerType}`
              : "Select a Professional"}
          </StepLabel>
        </Step>
        <Step>
          <StepLabel>Select Shift(s)</StepLabel>
        </Step>
        <Step>
          <StepLabel>Review Invites</StepLabel>
        </Step>
      </Stepper>

      {/* Display warning if worker has high call-off rate */}
      {frictionConfig.enabled && isDefined(selectedWorker) && hasHighCallOffRate && (
        <Alert
          severity="error"
          sx={{
            mt: 1,
            "& .MuiAlert-message": {
              color: "error.main",
            },
          }}
        >
          <strong>
            {workerFirstName} has same-day cancelled {totalCallOffs} time(s) in the last 30 days.
          </strong>{" "}
          Consider inviting someone else.
        </Alert>
      )}

      {(activeStep === 0 || !isDefined(selectedWorker)) && (
        <>
          {/* Show the confirmation prompt instead of search bar if worker has high call-offs */}
          {showHighCallOffConfirmation && (
            <HighCallOffConfirmation
              selectedWorker={selectedWorker}
              totalCallOffs={totalCallOffs}
              onInviteSomeoneElse={handleInviteSomeoneElse}
              onProceed={(confirmed) => {
                if (confirmed) {
                  setActiveStep(1);
                  validateAndUpdateShiftInvite({
                    selectedShiftInfo: {
                      start: zoneShiftStartTime,
                      end: utcToZonedTime(props.shiftInfo.end, facilityTimeZone),
                      shiftType: props.shiftInfo.shiftType as ShiftName,
                      isSelected: true,
                    },
                    selectedDate: startOfDay(zoneShiftStartTime),
                    selectedWorker,
                  });
                }
              }}
            />
          )}
          {showInviteRequiredConfirmation && (
            <InviteRequiredConfirmation
              selectedWorker={selectedWorker}
              onInviteSomeoneElse={handleInviteSomeoneElse}
              onProceed={(confirmed) => {
                if (confirmed) {
                  setActiveStep(1);
                  validateAndUpdateShiftInvite({
                    selectedShiftInfo: {
                      start: zoneShiftStartTime,
                      end: utcToZonedTime(props.shiftInfo.end, facilityTimeZone),
                      shiftType: props.shiftInfo.shiftType as ShiftName,
                      isSelected: true,
                    },
                    selectedDate: startOfDay(zoneShiftStartTime),
                    selectedWorker,
                  });
                }
              }}
            />
          )}
          {showSearchWorkersDropdown && (
            /* Show normal search interface if no high call-offs */
            <ShiftInviteNearbyWorkers
              selectedWorker={selectedWorker}
              setSelectedWorker={handleSetSelectedWorker}
              workerType={workerType}
              setActiveStep={setActiveStep}
              modalState={modalState}
              onClickNext={() => {
                if (selectedShiftStartsInFuture) {
                  validateAndUpdateShiftInvite({
                    selectedShiftInfo: {
                      start: zoneShiftStartTime,
                      end: utcToZonedTime(props.shiftInfo.end, facilityTimeZone),
                      shiftType: props.shiftInfo.shiftType as ShiftName,
                      isSelected: true,
                    },
                    selectedDate: startOfDay(zoneShiftStartTime),
                    selectedWorker,
                  });
                }
              }}
            ></ShiftInviteNearbyWorkers>
          )}
        </>
      )}

      {activeStep === 1 && (
        <GroupShiftInviteDetails
          selectedWorker={selectedWorker}
          setSelectedWorker={setSelectedWorker}
          setActiveStep={setActiveStep}
          shiftInviteDialogProps={props}
        />
      )}
      {activeStep === 2 && (
        <ShiftInviteReview
          modalState={modalState}
          workerType={workerType}
          setActiveStep={setActiveStep}
          selectedWorker={selectedWorker}
        ></ShiftInviteReview>
      )}
    </Stack>
  );
}
